import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { SignOut, EnvelopeSimple, FolderOpen, ArrowUpRight, UserPlus, Trash, ShieldStar, ArrowCounterClockwise, LockSimple, FolderSimple } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { useT } from "../context/LangContext";
import { Reveal } from "../components/Reveal";
import { DRIVE_FOLDER_URL } from "../lib/site";
import { TransparencySection } from "../components/TransparencySection";
import { MemberCard } from "../components/MemberCard";
import { RenewalNotice } from "../components/RenewalNotice";
import Accedi from "./Accedi";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPanel = ({ c }) => {
  const [members, setMembers] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const apply = (data) => {
    setMembers(data.members || []);
    setAdmins(data.admins || []);
  };

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/member/allowlist`, { withCredentials: true });
      apply(data);
    } catch {}
  }, []);

  useEffect(() => { load(); }, [load]);

  const call = async (fn, okMsg) => {
    setBusy(true);
    try {
      const { data } = await fn();
      apply(data);
      if (okMsg) toast.success(okMsg);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setBusy(false); }
  };

  const add = () => {
    if (!email.trim()) return;
    call(() => axios.post(`${API}/member/allowlist`, { email, name }, { withCredentials: true }), c.adminAdd + " ✓").then(() => { setEmail(""); setName(""); });
  };
  const remove = (em) => call(() => axios.delete(`${API}/member/allowlist/${encodeURIComponent(em)}`, { withCredentials: true }));
  const reset = (em) => call(() => axios.post(`${API}/member/allowlist/${encodeURIComponent(em)}/reset-password`, {}, { withCredentials: true }), c.adminResetDone);
  const toggleDocs = (em) => call(() => axios.post(`${API}/member/allowlist/${encodeURIComponent(em)}/docs-access`, {}, { withCredentials: true }));

  return (
    <Reveal delay={0.15} data-testid="admin-panel">
      <div className="rounded-2xl border border-border bg-surface p-8">
        <div className="flex items-center gap-3 mb-2">
          <ShieldStar size={30} weight="duotone" className="text-terracotta" />
          <h3 className="font-display font-bold tracking-tight text-xl text-ink">{c.adminTitle}</h3>
        </div>
        <p className="text-sm text-stone mb-6">{c.adminSub}</p>

        <div className="grid gap-3 sm:grid-cols-[1fr_1fr_auto] mb-6">
          <input data-testid="allowlist-name-input" type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder={c.adminNamePlaceholder}
            className="rounded-full border border-border px-5 py-3 text-ink outline-none focus:border-terracotta transition-colors duration-200" />
          <input data-testid="allowlist-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder={c.adminPlaceholder}
            className="rounded-full border border-border px-5 py-3 text-ink outline-none focus:border-terracotta transition-colors duration-200" />
          <button data-testid="allowlist-add-btn" onClick={add} disabled={busy} className="inline-flex items-center justify-center gap-2 rounded-full bg-terracotta px-6 py-3 font-semibold text-white transition-colors duration-200 hover:bg-terracotta-dark disabled:opacity-60">
            <UserPlus size={18} /> {c.adminAdd}
          </button>
        </div>

        <div className="flex items-center justify-between mb-3">
          <span className="font-display font-bold tracking-widest text-xs text-forest">{c.adminListLabel}</span>
          <span className="text-xs text-stone" data-testid="allowlist-count">{members.length} {c.adminCountLabel}</span>
        </div>
        <ul className="space-y-2 max-h-[28rem] overflow-y-auto pr-1" data-testid="allowlist">
          {admins.map((a) => (
            <li key={a} className="flex items-center justify-between rounded-xl bg-sand px-4 py-2.5 text-sm">
              <span className="text-ink break-all">{a}</span>
              <span className="rounded-full bg-forest/10 px-2.5 py-0.5 text-xs font-semibold text-forest">{c.adminBadge}</span>
            </li>
          ))}
          {members.map((m) => (
            <li key={m.email} className="flex flex-col sm:flex-row sm:items-center gap-2 rounded-xl bg-sand px-4 py-2.5 text-sm" data-testid={`member-${m.email}`}>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold text-ink">{m.name || m.email}</span>
                  {m.card && <span className="text-xs text-stone">{m.card}</span>}
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${m.has_password ? "bg-forest/10 text-forest" : "bg-ochre/15 text-ochre"}`}>
                    {m.has_password ? c.adminPwSet : c.adminPwMissing}
                  </span>
                  {m.docs_access && <span className="rounded-full bg-ink/10 px-2 py-0.5 text-[11px] font-semibold text-ink" data-testid={`docs-badge-${m.email}`}>{c.adminDocsBadge}</span>}
                </div>
                {m.name && <div className="text-xs text-stone break-all">{m.email}</div>}
              </div>
              <div className="flex items-center gap-4 shrink-0">
                <button data-testid={`docs-${m.email}`} onClick={() => toggleDocs(m.email)} disabled={busy} title={m.docs_access ? c.adminDocsRevoke : c.adminDocsGrant} className={`inline-flex items-center gap-1 transition-colors duration-200 ${m.docs_access ? "text-forest hover:text-ink" : "text-stone hover:text-ink"}`}>
                  <FolderSimple size={16} weight={m.docs_access ? "fill" : "regular"} /> {m.docs_access ? c.adminDocsRevoke : c.adminDocsGrant}
                </button>
                {m.has_password && (
                  <button data-testid={`reset-${m.email}`} onClick={() => reset(m.email)} disabled={busy} className="inline-flex items-center gap-1 text-ink hover:text-terracotta transition-colors duration-200">
                    <ArrowCounterClockwise size={16} /> {c.adminReset}
                  </button>
                )}
                <button data-testid={`remove-${m.email}`} onClick={() => remove(m.email)} disabled={busy} className="inline-flex items-center gap-1 text-terracotta hover:text-terracotta-dark transition-colors duration-200">
                  <Trash size={16} /> {c.adminRemove}
                </button>
              </div>
            </li>
          ))}
          {members.length === 0 && <li className="text-sm text-stone pt-1">{c.adminNone}</li>}
        </ul>
      </div>
    </Reveal>
  );
};

export default function AreaSoci() {
  const { user, loading, logout } = useAuth();
  const { t } = useT();
  const c = t.member;
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bone" data-testid="area-soci-loading">
        <div className="h-10 w-10 rounded-full border-2 border-terracotta border-t-transparent animate-spin" />
      </div>
    );
  }
  if (!user) return <Accedi />;

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const initials = (user?.name || user?.email || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  const isAdmin = user?.role === "admin";

  return (
    <section className="pt-28 md:pt-36 pb-14 md:pb-20" data-testid="area-soci">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <Reveal>
          <p className="font-display font-bold tracking-widest text-xs text-terracotta mb-4">{c.kicker}</p>
        </Reveal>

        <Reveal delay={0.05}>
          <div className="rounded-[2rem] border border-border bg-surface p-8 md:p-10 flex flex-col md:flex-row md:items-center gap-6">
            <div className="h-20 w-20 rounded-full bg-forest text-bone flex items-center justify-center font-display font-extrabold text-2xl">{initials}</div>
            <div className="flex-1">
              <h1 className="font-display font-extrabold tracking-tighter text-3xl md:text-4xl text-ink" data-testid="profile-name">
                {c.greeting} {user?.name || c.fallbackName}
              </h1>
              <p className="mt-1 inline-flex items-center gap-2 text-stone" data-testid="profile-email">
                <EnvelopeSimple size={17} weight="duotone" className="text-terracotta" /> {user?.email}
                {isAdmin && <span className="ml-2 rounded-full bg-forest/10 px-2.5 py-0.5 text-xs font-semibold text-forest">{c.adminBadge}</span>}
              </p>
            </div>
            <button data-testid="logout-btn" onClick={handleLogout} className="inline-flex items-center gap-2 rounded-full border border-ink/20 px-6 py-3 font-semibold text-ink transition-colors duration-200 hover:bg-ink hover:text-bone">
              <SignOut size={18} /> {c.logout}
            </button>
          </div>
        </Reveal>

        {user.card && (
          <div className="mt-6 space-y-6">
            <RenewalNotice card={user.card} c={c} />
            <MemberCard user={user} c={c} />
          </div>
        )}

        <div className="mt-6 grid gap-6 md:grid-cols-3">
          <Reveal delay={0.1} className="md:col-span-2">
            {user.docs_access ? (
              <div className="h-full rounded-2xl border border-border bg-sand p-8 flex flex-col" data-testid="docs-card">
                <FolderOpen size={38} weight="duotone" className="text-terracotta mb-5" />
                <h3 className="font-display font-bold tracking-tight text-xl text-ink mb-3">{c.driveTitle}</h3>
                <p className="text-sm text-stone leading-relaxed mb-6">{c.driveBody}</p>
                <a
                  href={DRIVE_FOLDER_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="drive-folder-link"
                  className="group mt-auto inline-flex items-center gap-2 self-start rounded-full bg-terracotta px-7 py-3.5 font-semibold text-white transition-colors duration-200 hover:bg-terracotta-dark"
                >
                  {c.driveButton}
                  <ArrowUpRight size={18} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
              </div>
            ) : (
              <div className="h-full rounded-2xl border border-border bg-sand p-8 flex flex-col" data-testid="docs-restricted">
                <LockSimple size={38} weight="duotone" className="text-stone mb-5" />
                <h3 className="font-display font-bold tracking-tight text-xl text-ink mb-3">{c.driveTitle}</h3>
                <p className="text-sm text-stone leading-relaxed">{c.driveRestricted}</p>
              </div>
            )}
          </Reveal>
          <Reveal delay={0.15}>
            <div className="h-full rounded-2xl bg-forest text-bone p-8">
              <h3 className="font-display font-bold tracking-tight text-xl mb-3">{c.cards[2].title}</h3>
              <p className="text-sm text-bone/80 leading-relaxed">{c.cards[2].body}</p>
            </div>
          </Reveal>
        </div>

        {isAdmin && (
          <div className="mt-8">
            <AdminPanel c={c} />
          </div>
        )}

        <TransparencySection />
      </div>
    </section>
  );
}
